import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, resolveCharIdFromJwt } from '@/lib/axios';
import { sortMessagesChronologically } from '@/pages/messages/messageUtils';
import { filterThreadMessages } from '@/pages/messages/messageBelongsToThread';

export interface Conversation {
  id: number;
  sender_CharID: number;
  receiver_CharID: number;
  message: string;
  status: string;
  created_at: string;
  otherCharName: string;
  otherRefObjID: number;
  otherCharID: number;
  unreadCount: number;
}

export interface ChatMessage {
  id: number;
  sender_CharID: number;
  receiver_CharID: number;
  id64: number | null;
  message: string;
  status: string;
  created_at: string;
  senderName: string;
  senderRefObjID: number;
  receiverName: string;
  receiverRefObjID: number;
  itemName?: string | null;
  itemIcon?: string;
  isLocked: number;
}

export interface OnlinePlayer {
  CharID: number;
  CharName16: string;
  RefObjID: number;
}

export interface MessageInventoryItem {
  id64: number;
  slot: number;
  name: string;
  icon: string;
  optLevel: number;
  isLocked: boolean;
}

export interface SendMessageResult {
  messageId: number;
  timestamp: string;
}

let authCharIdFallback = 0;

/** Sincroniza charId desde useAuth (user.charId tras validate-session). */
export function setAuthCharIdFallback(charId: number) {
  authCharIdFallback = charId > 0 ? charId : 0;
}

export function resetAuthCharIdFallback() {
  authCharIdFallback = 0;
}

/**
 * charId canónico para mensajes — JWT en localStorage (paridad vsro.me).
 */
export function getMessagesCharId(): number {
  const fromJwt = resolveCharIdFromJwt();
  if (fromJwt > 0) return fromJwt;
  return authCharIdFallback;
}

export function conversationsQueryKey(charId: number): (string | number)[] {
  return ['messages', 'conversations', charId];
}

export function messagesThreadQueryKey(charId: number, otherCharId: number): (string | number)[] {
  return ['messages', 'thread', charId, otherCharId];
}

export function unreadCountQueryKey(charId: number): (string | number)[] {
  return ['messages', 'unread-count', charId];
}

export function onlinePlayersQueryKey(charId: number, search: string): (string | number)[] {
  return ['messages', 'online', charId, search];
}

export function messageInventoryQueryKey(charId: number): (string | number)[] {
  return ['messages', 'inventory', charId];
}

function normalizeConversation(row: Conversation): Conversation {
  return {
    ...row,
    id: Number(row.id),
    sender_CharID: Number(row.sender_CharID),
    receiver_CharID: Number(row.receiver_CharID),
    otherCharID: Number(row.otherCharID),
    otherRefObjID: Number(row.otherRefObjID),
    unreadCount: Number(row.unreadCount ?? 0),
    message: String(row.message ?? ''),
    otherCharName: String(row.otherCharName ?? ''),
    created_at: String(row.created_at ?? ''),
  };
}

export async function fetchConversationsList(): Promise<Conversation[]> {
  const { data } = await api.get<{ status: string; data: Conversation[] }>('/api/v1/messages/conversations');
  return (data.data ?? [])
    .map((row) => normalizeConversation(row))
    .filter((row) => row.otherCharID > 0 && !Number.isNaN(row.otherCharID));
}

function sortConversations(list: Conversation[]): Conversation[] {
  return [...list].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

function conversationTimestamp(row: Conversation): number {
  const parsed = Date.parse(String(row.created_at ?? ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

export function mergeConversationLists(cached: Conversation[], fresh: Conversation[]): Conversation[] {
  const merged = new Map<number, Conversation>();

  const consider = (row: Conversation) => {
    const normalized = normalizeConversation(row);
    const id = normalized.otherCharID;
    if (!id || id <= 0 || Number.isNaN(id)) return;

    const prev = merged.get(id);
    if (!prev) {
      merged.set(id, normalized);
      return;
    }

    const prevTime = conversationTimestamp(prev);
    const nextTime = conversationTimestamp(normalized);
    const newer = nextTime >= prevTime ? normalized : prev;
    const older = nextTime >= prevTime ? prev : normalized;

    merged.set(id, {
      ...newer,
      otherCharName: newer.otherCharName || older.otherCharName,
      otherRefObjID: newer.otherRefObjID || older.otherRefObjID,
      unreadCount: Math.max(prev.unreadCount, normalized.unreadCount),
    });
  };

  for (const row of fresh) consider(row);
  for (const row of cached) consider(row);

  return sortConversations([...merged.values()]);
}

const conversationsSyncTimers = new Map<number, ReturnType<typeof setTimeout>>();

export async function syncConversations(queryClient: QueryClient, charId: number): Promise<void> {
  if (charId <= 0) return;
  const cached = queryClient.getQueryData<Conversation[]>(conversationsQueryKey(charId)) ?? [];
  try {
    const fresh = await fetchConversationsList();
    queryClient.setQueryData(
      conversationsQueryKey(charId),
      mergeConversationLists(cached, fresh),
    );
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[messages] syncConversations failed', err);
    }
  }
}

export function refetchConversationsList(queryClient: QueryClient, charId: number) {
  if (charId <= 0) return Promise.resolve();
  return queryClient.refetchQueries({ queryKey: conversationsQueryKey(charId) });
}

export function replaceConversations(queryClient: QueryClient, charId: number, list: Conversation[]) {
  queryClient.setQueryData(conversationsQueryKey(charId), sortConversations(list.map(normalizeConversation)));
}

export function upsertConversationInCache(
  queryClient: QueryClient,
  charId: number,
  patch: {
    otherCharID: number;
    otherCharName: string;
    otherRefObjID: number;
    message: string;
    created_at: string;
    senderCharID?: number;
    unreadDelta?: number;
  },
): boolean {
  const otherId = Number(patch.otherCharID);
  if (!otherId || Number.isNaN(otherId)) return false;

  let wasNew = false;
  queryClient.setQueryData<Conversation[]>(conversationsQueryKey(charId), (prev) => {
    const list = prev ?? [];
    const idx = list.findIndex((c) => Number(c.otherCharID) === otherId);
    wasNew = idx < 0;
    const existing = idx >= 0 ? list[idx] : null;
    const senderCharID = patch.senderCharID ?? charId;
    const row: Conversation = normalizeConversation({
      id: existing?.id ?? 0,
      sender_CharID: senderCharID,
      receiver_CharID: senderCharID === charId ? otherId : charId,
      message: patch.message,
      status: existing?.status ?? 'unread',
      created_at: patch.created_at,
      otherCharName: patch.otherCharName || existing?.otherCharName || '',
      otherRefObjID: patch.otherRefObjID || existing?.otherRefObjID || 0,
      otherCharID: otherId,
      unreadCount: Math.max(0, (existing?.unreadCount ?? 0) + (patch.unreadDelta ?? 0)),
    });
    if (idx >= 0) {
      const next = [...list];
      next.splice(idx, 1);
      return sortConversations([row, ...next]);
    }
    return sortConversations([row, ...list]);
  });
  return wasNew;
}

export function refreshMessagesUnreadCount(queryClient: QueryClient, charId: number) {
  if (charId <= 0) return Promise.resolve();
  return queryClient.refetchQueries({ queryKey: unreadCountQueryKey(charId) });
}

function applyMarkReadOptimistic(
  queryClient: QueryClient,
  charId: number,
  otherCharId: number,
): number {
  const convs = queryClient.getQueryData<Conversation[]>(conversationsQueryKey(charId)) ?? [];
  const conv = convs.find((c) => Number(c.otherCharID) === otherCharId);
  const cleared = conv?.unreadCount ?? 0;

  queryClient.setQueryData<Conversation[]>(conversationsQueryKey(charId), (prev) =>
    (prev ?? []).map((c) =>
      Number(c.otherCharID) === otherCharId ? { ...c, unreadCount: 0 } : c,
    ),
  );

  queryClient.setQueryData<ChatMessage[]>(messagesThreadQueryKey(charId, otherCharId), (prev) =>
    (prev ?? []).map((m) =>
      m.receiver_CharID === charId && m.sender_CharID === otherCharId && m.status === 'unread'
        ? { ...m, status: 'read' }
        : m,
    ),
  );

  if (cleared > 0) {
    queryClient.setQueryData<number>(unreadCountQueryKey(charId), (prev) =>
      Math.max(0, (prev ?? 0) - cleared),
    );
  }

  return cleared;
}

export async function postMarkConversationRead(otherCharId: number): Promise<void> {
  await api.post(`/api/v1/messages/${otherCharId}/read`);
}

export function applyMarkReadInCache(
  queryClient: QueryClient,
  charId: number,
  otherCharId: number,
): number {
  return applyMarkReadOptimistic(queryClient, charId, otherCharId);
}

export function scheduleConversationsSync(queryClient: QueryClient, charId: number, delayMs = 400) {
  if (charId <= 0) return;
  const pending = conversationsSyncTimers.get(charId);
  if (pending) clearTimeout(pending);
  conversationsSyncTimers.set(
    charId,
    window.setTimeout(() => {
      conversationsSyncTimers.delete(charId);
      void syncConversations(queryClient, charId);
    }, delayMs),
  );
}

async function fetchMessages(
  otherCharId: number,
  charId: number,
  offset = 0,
  limit = 50,
): Promise<ChatMessage[]> {
  if (charId <= 0 || otherCharId <= 0) return [];
  const { data } = await api.get<{ status: string; data: ChatMessage[] }>(
    `/api/v1/messages/${otherCharId}`,
    { params: { offset, limit } },
  );
  const rows = filterThreadMessages(data.data ?? [], charId, otherCharId);
  return sortMessagesChronologically(rows);
}

async function fetchUnreadCount(): Promise<number> {
  const { data } = await api.get<{ status: string; data: { count: number } }>(
    '/api/v1/messages/unread-count',
  );
  return data.data?.count ?? 0;
}

async function fetchOnlinePlayers(search: string): Promise<OnlinePlayer[]> {
  const { data } = await api.get<{ status: string; data: OnlinePlayer[] }>(
    '/api/v1/messages/online',
    { params: search ? { search } : undefined },
  );
  return data.data ?? [];
}

async function fetchMessageInventory(): Promise<MessageInventoryItem[]> {
  const { data } = await api.get<{ status: string; data: MessageInventoryItem[] }>(
    '/api/v1/messages/inventory',
  );
  return data.data ?? [];
}

export function useConversations(charIdOverride?: number) {
  const queryClient = useQueryClient();
  const charId = charIdOverride ?? getMessagesCharId();
  return useQuery({
    queryKey: conversationsQueryKey(charId),
    queryFn: async () => {
      const fresh = await fetchConversationsList();
      const cached = queryClient.getQueryData<Conversation[]>(conversationsQueryKey(charId)) ?? [];
      return mergeConversationLists(cached, fresh);
    },
    enabled: charId > 0,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    refetchInterval: 15_000,
  });
}

export function useMessagesThread(otherCharId: number | null, charIdOverride?: number) {
  const charId = charIdOverride ?? getMessagesCharId();
  return useQuery({
    queryKey: messagesThreadQueryKey(charId, otherCharId ?? 0),
    queryFn: () => fetchMessages(otherCharId!, charId),
    enabled: charId > 0 && otherCharId !== null && otherCharId > 0,
    staleTime: 0,
    gcTime: 5 * 60_000,
    refetchOnMount: 'always',
    retry: 2,
  });
}

export function useUnreadMessagesCount(charIdOverride?: number) {
  const charId = charIdOverride ?? getMessagesCharId();
  return useQuery({
    queryKey: unreadCountQueryKey(charId),
    queryFn: fetchUnreadCount,
    enabled: charId > 0,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

export function useOnlinePlayers(search: string) {
  const charId = getMessagesCharId();
  return useQuery({
    queryKey: onlinePlayersQueryKey(charId, search),
    queryFn: () => fetchOnlinePlayers(search),
    enabled: charId > 0,
    staleTime: 15_000,
  });
}

export function useMessageInventory(enabled: boolean) {
  const charId = getMessagesCharId();
  return useQuery({
    queryKey: messageInventoryQueryKey(charId),
    queryFn: fetchMessageInventory,
    enabled: charId > 0 && enabled,
    staleTime: 30_000,
  });
}

export function useSendMessage() {
  return useMutation({
    mutationFn: async (payload: {
      receiver: number;
      message: string;
      id64?: number | null;
    }): Promise<SendMessageResult> => {
      const { data } = await api.post<{ status: string; data: SendMessageResult }>(
        '/api/v1/messages/send',
        payload,
      );
      return data.data;
    },
  });
}

export function useMarkRead(charIdOverride?: number) {
  const queryClient = useQueryClient();
  const charId = charIdOverride ?? getMessagesCharId();

  return useMutation({
    mutationFn: async (otherCharId: number) => {
      await api.post(`/api/v1/messages/${otherCharId}/read`);
    },
    onMutate: (otherCharId) => {
      applyMarkReadOptimistic(queryClient, charId, otherCharId);
    },
    onSuccess: (_data, otherCharId) => {
      scheduleConversationsSync(queryClient, charId, 500);
      void refreshMessagesUnreadCount(queryClient, charId);
      void queryClient.invalidateQueries({
        queryKey: messagesThreadQueryKey(charId, otherCharId),
      });
    },
    onError: () => {
      void refreshMessagesUnreadCount(queryClient, charId);
      scheduleConversationsSync(queryClient, charId);
    },
  });
}

export async function markConversationRead(
  queryClient: QueryClient,
  charId: number,
  otherCharId: number,
): Promise<void> {
  if (charId <= 0 || otherCharId <= 0) return;
  applyMarkReadOptimistic(queryClient, charId, otherCharId);
  try {
    await postMarkConversationRead(otherCharId);
    scheduleConversationsSync(queryClient, charId, 500);
  } finally {
    await refreshMessagesUnreadCount(queryClient, charId);
  }
}

export function prefetchMessagesQueries(queryClient: QueryClient, charId: number) {
  if (charId <= 0) return;
  void queryClient.prefetchQuery({
    queryKey: conversationsQueryKey(charId),
    queryFn: fetchConversationsList,
    staleTime: 0,
  });
  void queryClient.prefetchQuery({
    queryKey: unreadCountQueryKey(charId),
    queryFn: fetchUnreadCount,
    staleTime: 0,
  });
}

export function invalidateMessagesQueries(queryClient: QueryClient, charId: number) {
  void queryClient.invalidateQueries({ queryKey: ['messages', charId] });
  void queryClient.invalidateQueries({ queryKey: conversationsQueryKey(charId) });
  void queryClient.invalidateQueries({ queryKey: unreadCountQueryKey(charId) });
}
